import { HyperlinkButton } from '@/design-system/components/buttons/hyperlinkButton/HyperlinkButton';
import { Divider } from '@/design-system/components/Divider';
import { ModalView, useModalView } from '@/design-system/components/modal';
import { getObjectScreenNameFromPath, popStateLockedMap } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { H5, TextBody } from '@/design-system/components/typography';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { ScrollView, SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import BillPaymentApp from '@/features/bill-payment/index';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { mapUrlLink, onGoBack } from '@/features/bill-payment/utils/utils';

export interface UnionPayMoreWebProps {}

export const UnionPayMoreWebComponent: React.FC<UnionPayMoreWebProps> = ({}) => {
  const { t } = useTranslation('global-bill-payment');
  const navigation = useNavigation();
  const { useModalProps } = useModalView({});

  const onBack = () => {
    if (Platform.OS === 'web') {
      popStateLockedMap.set('popstate', true);
      onGoBack(navigation);
      setTimeout(() => popStateLockedMap.set('popstate', false), 500);
    } else {
      onGoBack(navigation);
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        const onPopState = (event: any) => {
          window.history.pushState(event.state, '');
          setTimeout(() => {
            const popStateLocked = popStateLockedMap.get('popstate') ?? false;
            if (!document.location.href || popStateLocked) return;
            const { parentName, subName } = mapUrlLink(document.location.href);
            var screenNameObject = getObjectScreenNameFromPath([BillPaymentApp], `/${subName}`);
            if (
              screenNameObject.screenName &&
              screenNameObject.routeStackName === parentName &&
              screenNameObject.screenName !== BillPaymentStackRoutes.Landing
            ) {
              onBack();
            }
          }, 200);
        };
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  const [currency, setCurrency] = useState('HKD');
  const [amount, setAmount] = useState(undefined);
  const [amountRmb, setAmountRmb] = useState(undefined);

  return (
    <ModalView useModalProps={useModalProps} minWidth={500}>
      <View sx={{ ...sxStyles.container }}>
        <ScrollView sx={sxStyles.scrollView} showsVerticalScrollIndicator={false}>
          <View sx={sxStyles.body}>
            <View>
              <View sx={sxStyles.toolbar}>
                <HyperlinkButton
                  icon="arrowLeft"
                  iconPosition="left"
                  iconSize={20}
                  label={t('global-bill-payment::glossary.back-to-payment-overview')}
                  onPress={() => onBack()}
                />
              </View>

              <View sx={sxStyles.viewRow}>
                <View sx={sxStyles.spaceX} />
                <H5 bold sx={sxStyles.title}>
                  More
                </H5>
                <View sx={sxStyles.spaceX} />
              </View>
              <View sx={{ alignItems: 'center', gap: '$4' }}>
                <TextBody sx={{ color: '$textPrimary', pt: '$4' }}>UnionPay Payment FAQ</TextBody>
                <Divider sx={{ margin: 'auto', backgroundColor: '$borderField', width: '80%' }} />
                <TextBody sx={{ color: '$textPrimary', pt: '$4' }}>Quit UnionPay Payment Instruction</TextBody>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </ModalView>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: 'transparent',
    flex: 1
  },
  scrollView: {
    padding: '$3',
    maxHeight: 700
  },
  body: {
    backgroundColor: '$backgroundPrimary',
    flex: 1,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10,
    overflow: 'hidden',
    borderRadius: '$radius3'
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: '$7',
    backgroundColor: '$backgroundSecondary'
  },
  title: {
    flex: 142,
    pt: '$10',
    pb: '$5'
  },
  spaceX: {
    flex: 12
  },
  viewRow: {
    flexDirection: 'row'
  }
};
